import type { VizDirective } from '../../../Generated/VizDirective.ts'
import type { ICurrentReport, WorkspaceItemMeta, WorkspaceNote } from './ChatSlice.js'
import { directiveKey, graphLabel } from './chatViz.js'

/**
 * The unified WORKSPACE item model (W-2). The right panel is ONE scrollable list of item-cards — charts, the
 * report, and manual notes — each derived from EXISTING slice state (no second source of truth): every
 * `openGraphs` entry → a `chart` item, `currentReport` (when it has blocks) → one `report` item, and each
 * `notes` entry → a `note` item. `pinned` reflects `pinnedItemIds`; `title`/`subtitle` are the resolved card
 * meta (an `itemMeta` override for chart/report, or the note's own fields, else a sensible derived default).
 *
 * A card WRAPS the existing rendering — it never reimplements it. The item carries just enough to render + a
 * `ref` back to its source (the chart directive, the report, or the note), so the panel can wrap `GraphPanel` /
 * `ReportPanel` / a note editor without the derivation layer knowing anything about the DOM.
 */
export type WorkspaceItem =
  | {
      id: string
      kind: 'chart'
      pinned: boolean
      title?: string
      subtitle?: string
      /** Free-text per-item note/context (see WorkspaceItemMeta.note). Chart/report only. */
      note?: string
      /** Conversation node (assistant turn) that created this chart — backlink target (see setCurrentNode). */
      sourceNodeId?: string
      ref: { directive: VizDirective }
    }
  | {
      id: string
      kind: 'report'
      pinned: boolean
      title?: string
      subtitle?: string
      note?: string
      ref: { kind: 'report' }
    }
  | { id: string; kind: 'note'; pinned: boolean; title?: string; subtitle?: string; ref: { note: WorkspaceNote } }

/** The stable workspace-item id for a chart directive (matches the id used by `togglePinItem`/`closeGraph`). */
export function chartItemId(directive: VizDirective): string {
  return `chart:${directiveKey(directive)}`
}

/** The single report item's fixed id. */
export const REPORT_ITEM_ID = 'report'

/** The stable workspace-item id for a note. */
export function noteItemId(note: Pick<WorkspaceNote, 'id'>): string {
  return `note:${note.id}`
}

/** First non-empty of the candidates, trimmed; else undefined. Used to resolve a card's title/subtitle. */
function firstText(...candidates: (string | undefined)[]): string | undefined {
  for (const c of candidates) {
    const t = c?.trim()
    if (t) return t
  }
  return undefined
}

/**
 * Derive the ordered {@link WorkspaceItem} list from existing slice state (pure). Natural order is charts (open
 * order) → report → notes; `pinned` is set from `pinnedItemIds`. The panel groups pinned-first for display —
 * see {@link partitionByPinned}. A chart/report card title falls back to its derived label; a note keeps its own.
 */
export function deriveWorkspaceItems(args: {
  openGraphs: VizDirective[]
  report: ICurrentReport
  notes: WorkspaceNote[]
  pinnedItemIds: string[]
  itemMeta: Record<string, WorkspaceItemMeta>
}): WorkspaceItem[] {
  const { openGraphs, report, notes, pinnedItemIds, itemMeta } = args
  const pinned = new Set(pinnedItemIds)
  const items: WorkspaceItem[] = []

  for (const directive of openGraphs) {
    const id = chartItemId(directive)
    const meta = itemMeta[id]
    items.push({
      id,
      kind: 'chart',
      pinned: pinned.has(id),
      title: firstText(meta?.title, graphLabel(directive)),
      subtitle: firstText(meta?.subtitle),
      note: meta?.note,
      sourceNodeId: meta?.sourceNodeId,
      ref: { directive }
    })
  }

  if (report.blocks.length > 0) {
    const meta = itemMeta[REPORT_ITEM_ID]
    items.push({
      id: REPORT_ITEM_ID,
      kind: 'report',
      pinned: pinned.has(REPORT_ITEM_ID),
      title: firstText(meta?.title, report.title, 'Report'),
      subtitle: firstText(meta?.subtitle),
      note: meta?.note,
      ref: { kind: 'report' }
    })
  }

  for (const note of notes) {
    const id = noteItemId(note)
    items.push({
      id,
      kind: 'note',
      pinned: pinned.has(id),
      title: firstText(note.title),
      subtitle: firstText(note.subtitle),
      ref: { note }
    })
  }

  return items
}

/** Split derived items into pinned (top) and staged (below), each preserving natural order. */
export function partitionByPinned(items: WorkspaceItem[]): { pinned: WorkspaceItem[]; staged: WorkspaceItem[] } {
  return {
    pinned: items.filter((i) => i.pinned),
    staged: items.filter((i) => !i.pinned)
  }
}
